import {Component, Injector, OnDestroy, OnInit} from '@angular/core';
import {
    AbstractStepTransactionComponent,
    BreadcrumbItem,
    BreadcrumbItemBuilder,
    EdocEventType,
    EdocMessage,
    EdocOptions,
    NotificationType,
    PesAlertModel,
    PesAlertModelBuilder,
    PesDocument,
    PesEventUtils,
    PesFormatNamingContextRegistry,
    PesModalService,
    PesStyle,
    PesStyleType,
    PesTask,
    PesTaskService,
    ScrollService,
    Step,
    StepButtonBuilder,
    SubscriptionManager,
    ValidateSequentialStep
} from '@pes/ui';
import {ISubscription} from 'rxjs/Subscription';
import {TranslateService} from '@ngx-translate/core';

/**
 * The view loaded into a circuit for validating documents sequentially.
 */
@Component({
    selector: 'action-validate-sequential',
    templateUrl: './step-validate-sequential.component.html'
})
export class StepValidateSequentialComponent extends AbstractStepTransactionComponent implements OnInit, OnDestroy {

    /**
     * The reference to the view name.
     */
    public static readonly VIEW_REF: string = 'StepValidateSequentialComponent';
    /**
     * The reference to the event responsible for initializing the list of documents to validate.
     */
    private static readonly INIT_EVENT: string = 'init_validate_sequential_step';
    /**
     * @inheritDoc
     */
    public step: ValidateSequentialStep = null;
    /**
     * The styles for all PES cards in this view.
     */
    protected cardStyle: PesStyle = null;
    /**
     * The reference to the PES task service.
     */
    protected taskService: PesTaskService = null;
    /**
     * The reference to the PES modal service.
     */
    protected modal: PesModalService = null;
    /**
     * The list of documents to validate.
     */
    protected documents: PesDocument[] = null;

    /**
     * The list of breadcrumb items.
     */
    protected breadcrumbItems: BreadcrumbItem[] = null;

    /**
     * The reference to the current document position in the list of documents to validate.
     */
    protected currentItemNum: number = 0;
    /**
     * The reference to the document currently be displayed.
     */
    protected currentDoc: PesDocument = null;
    /**
     * Indicates the number of document read in the reading required stack.
     */
    protected readDocStackNum: number = 0;
    /**
     * Indicates whether the current document has been read (true), or not (false).
     */
    protected docRead: boolean = false;
    /**
     * The reference to the PES translation service.
     */
    private readonly _translate: TranslateService = null;
    /**
     * A map that contains references for all transactions Edoc options.
     */
    private readonly OPTIONS_MAP: Map<string, EdocOptions> = null;
    /**
     * Reference to the PES ScrollService service.
     */
    private readonly _scrollService: ScrollService = null;

    /**
     * Creates a new StepValidateSequentialComponent instance.
     *
     * @param {Injector} injector the reference to the Angular Injector service.
     */
    constructor(protected injector: Injector) {
        super(injector, StepValidateSequentialComponent.VIEW_REF);
        this.taskService = injector.get(PesTaskService);
        this.modal = injector.get(PesModalService);
        this._translate = injector.get(TranslateService);
        this.OPTIONS_MAP = new Map<string, EdocOptions>();
        this._scrollService = injector.get(ScrollService);
        SubscriptionManager.getInstance().createSubscribable(this);
    }

    /**
     * @inheritDoc
     */
    public ngOnInit(): void {
        const taskRef: string = this.task.reference;
        this.orchestrator.forceLoading(true);
        this.step.transactions.forEach((trId: string) => {
            this.initEdocOptions(trId);
        });
        const sub: ISubscription = this.taskService.sendMessage(taskRef, PesEventUtils.EVENT_REF, {
            event: StepValidateSequentialComponent.INIT_EVENT,
            step: this.step
        }).subscribe(() => {
            const sub2: ISubscription = this.taskService.get(taskRef).subscribe((task: PesTask) => {
                const builder: BreadcrumbItemBuilder = BreadcrumbItemBuilder.create();
                const stepId: string = this.step.id;
                this.task = task;
                this.step = task.data.steps.find((step: Step) => {
                    return step.id === stepId;
                });
                const technicalContext: any = this.step.technicalContext;
                if (technicalContext && technicalContext.documents) {
                    this.breadcrumbItems = [];
                    this.documents = [];
                    technicalContext.documents.forEach((doc: PesDocument) => {
                        const docName: string = this._translate.instant(doc.configName);
                        this.documents.push(doc);
                        this.breadcrumbItems.push(
                            builder.reset().title(docName).clickEnable(false).build()
                        );
                    });
                    this.currentItemNum = 1;
                    this.setCurrentDoc();
                    this.readDocStackNum = this.documents.length;
                }
                this.initStepButtons();
                this.orchestrator.forceLoading(false);
            });
            SubscriptionManager.getInstance().registerSubscription(this, sub2);
        });
        SubscriptionManager.getInstance().registerSubscription(this, sub);
    }

    /**
     * @inheritDoc
     */
    public ngOnDestroy(): void {
        SubscriptionManager.getInstance().deleteSubscribable(this);
    }

    /**
     * @inheritDoc
     */
    public initStyles(styles: PesStyle[]): void {
        super.initStyles(styles);
        if (styles) {
            this.cardStyle = styles.find((style: PesStyle) => {
                return style.type === PesStyleType.CARD;
            });
        }
    }

    /**
     * The event handler that receives edoc events when enableContraliaEvent is true.
     *
     * @param {EdocMessage} message a message event fired by the edoc component.
     */
    protected contraliaEventHandler(message: EdocMessage): void {
        if (message.name === EdocEventType.EDOC_DOCREAD) {
            this.readDocStackNum--;
            this.docRead = true;
        }
    }

    /**
     * Initializes the Edoc options associated with the specified transaction.
     *
     * @param {string} transaction the id of the transaction for which to get the options.
     */
    protected initEdocOptions(transactionId: string): void {
        if (this.step.edocOptions) {
            if (!this.OPTIONS_MAP.has(transactionId)) {
                const options: EdocOptions = this.step.edocOptions.find((opt: EdocOptions) => {
                    return opt.transaction === transactionId;
                });
                this.OPTIONS_MAP.set(transactionId, options);
                if (options && this.documents) {
                    const size: number =
                        this.documents.filter((doc: PesDocument) => doc.transactionId === transactionId).length;
                    this.readDocStackNum += size;
                }
            }
        } else {
            this.OPTIONS_MAP.set(transactionId, null);
        }
    }

    /**
     * Sets the current document, depending on the current item num.
     */
    protected setCurrentDoc(): void {
        this.currentDoc = this.documents[this.currentItemNum - 1];
        this.docRead = !this.readingForced();
    }

    /**
     * @inheritDoc
     */
    protected initStepButtons(): void {
        this.stepButtons = [
            StepButtonBuilder.build(
                this.step.positiveLabel || 'validate_sequential_step.submit',
                NotificationType.PRIMARY,
                this.submit.bind(this)
            )
        ];
        this.notifyInitStepButtons();
    }

    /**
     * @inheritDoc
     */
    protected submit(): void {
        if (!this.docRead) {
            this.forceDocReadingModal();
        } else if (this.documents && this.currentItemNum < this.documents.length) {
            this.currentItemNum += 1;
            this.setCurrentDoc();
            this._scrollService.scrollTo();
        } else {
            const sub: ISubscription = this.orchestrator.checkValidAndSubmit({
                stepIdx: this.task.data.stepIdx,
                taskData: this.task.data.taskData
            }).subscribe(() => {
                this.stepEventService.doExitStepEvent(this);
                this.orchestrator.goToNext();
            });
            SubscriptionManager.getInstance().registerSubscription(this, sub);
        }
    }

    /**
     * Displayes the modal to indicated that the user must entirelly read the current document..
     */
    protected forceDocReadingModal(): void {
        const model: PesAlertModel = PesAlertModelBuilder.build(
            'validate_sequential_step.dialog.force_reading.title',
            'validate_sequential_step.dialog.force_reading.message'
        );
        const sub: ISubscription = this.modal.alert(model).subscribe();
        SubscriptionManager.getInstance().registerSubscription(this, sub);
    }

    /**
     * Returns a boolean that indicates whether the documents in the specified transaction must be entirely read.
     *
     * @param {string} transactionId the id of the transaction for which to get the force reading option.
     *
     * @returns {boolean} true whether the documents in the specified transaction must be entirely read; false
     *                    otherwise.
     */
    protected readingForced(): boolean {
        let result: boolean = false;
        if (this.currentDoc) {
            result = (this.currentDoc as any).forceReading
        }
        return result;
    }
}

PesFormatNamingContextRegistry.getInstance().delete(StepValidateSequentialComponent.VIEW_REF);
PesFormatNamingContextRegistry.getInstance().add(StepValidateSequentialComponent.VIEW_REF, StepValidateSequentialComponent);



// WEBPACK FOOTER //
// ./src/app/component/step-validate-sequential/step-validate-sequential.component.ts